import { motion } from 'framer-motion'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'
import styles from './Features.module.css'

const features = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M3 9h18M9 21V9"/>
      </svg>
    ),
    title: 'Context Storage',
    problem: '99% of your DB is just LLM messages',
    solution: 'Luminox handles context storage via PostgreSQL, Redis, and S3. Support for ChatGPT, Gemini, Anthropic, images, audio, and files.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    title: 'Long-Running Agents',
    problem: 'Managing state is a nightmare',
    solution: 'Built-in context editing methods and a TODO agent out of the box. Managing agent state becomes a piece of cake.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    ),
    title: 'Agent Observability',
    problem: "You can't see how your agent is doing",
    solution: "Track tasks per session and monitor your agent's actual success rate. Stop obsessing over token costs — improve the agent first.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    title: 'Agent Self-Learning',
    problem: 'Your agent is hit or miss',
    solution: 'Experience agent remembers successful runs and converts them into reusable tool-use SOPs. Consistency is everything.',
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
}

export default function Features() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>()

  return (
    <section id="features" className={styles.features} ref={ref}>
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 20 }}
        animate={isVisible ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
      >
        <span className="section-tag">Why Luminox?</span>
        <h2 className="section-title">
          Solve the 4 Biggest Problems<br />in Agent Development
        </h2>
      </motion.div>

      <motion.div
        className={styles.grid}
        variants={containerVariants}
        initial="hidden"
        animate={isVisible ? 'visible' : 'hidden'}
      >
        {features.map((feature) => (
          <motion.div
            key={feature.title}
            className={styles.card}
            variants={itemVariants}
            whileHover={{
              y: -8,
              borderColor: 'rgba(255, 215, 0, 0.3)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            }}
          >
            <motion.div
              className={styles.icon}
              whileHover={{ scale: 1.1, rotate: 5 }}
            >
              {feature.icon}
            </motion.div>
            <h3>{feature.title}</h3>
            <p className={styles.problem}>{feature.problem}</p>
            <p className={styles.solution}>{feature.solution}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  )
}
